import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Eye, Type, MousePointer2, Accessibility } from 'lucide-react';

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    highlightLinks: false,
    bigCursor: false,
    readableFont: false,
    textSpacing: false,
  });

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${settings.fontSize}%`;
    
    // High contrast
    if (settings.contrast === 'high') {
      root.classList.add('high-contrast');
    } else if (settings.contrast === 'inverted') {
      root.classList.add('inverted-colors');
    } else {
      root.classList.remove('high-contrast', 'inverted-colors');
    }
    
    // Highlight links
    if (settings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
    
    // Big cursor
    if (settings.bigCursor) {
      root.classList.add('big-cursor');
    } else {
      root.classList.remove('big-cursor');
    }
    
    // Readable font
    if (settings.readableFont) {
      root.classList.add('readable-font');
    } else {
      root.classList.remove('readable-font');
    }
    
    // Text spacing
    if (settings.textSpacing) {
      root.classList.add('text-spacing');
    } else {
      root.classList.remove('text-spacing');
    }
    
    // Save to localStorage
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  }, [settings]);

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load accessibility settings');
      }
    }
  }, []);

  const resetSettings = () => {
    setSettings({
      fontSize: 100,
      contrast: 'normal',
      highlightLinks: false,
      bigCursor: false,
      readableFont: false,
      textSpacing: false,
    });
  };

  const adjustFontSize = (delta) => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(150, Math.max(80, prev.fontSize + delta))
    }));
  };

  return (
    <>
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 flex items-center justify-center group"
        aria-label="Open Accessibility Options"
        title="Accessibility Options"
      >
        <Accessibility className="w-7 h-7" />
        <span className="sr-only">Accessibility Options</span>
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-foreground/50 z-50"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="fixed left-6 bottom-24 z-50 w-80 max-h-[80vh] bg-card rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Accessibility className="w-6 h-6" />
                <span className="font-semibold">Accessibility Options</span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-lg bg-primary-foreground/20 flex items-center justify-center hover:bg-primary-foreground/30 transition-colors"
                aria-label="Close accessibility panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              
              {/* Font Size */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Type className="w-4 h-4 text-primary" />
                  Text Size: {settings.fontSize}%
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustFontSize(-10)}
                    className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="Decrease text size"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all"
                      style={{ width: `${((settings.fontSize - 80) / 70) * 100}%` }}
                    />
                  </div>
                  <button
                    onClick={() => adjustFontSize(10)}
                    className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="Increase text size"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Contrast Options */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  Color Contrast
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'High' },
                    { value: 'inverted', label: 'Inverted' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSettings(prev => ({ ...prev, contrast: option.value }))}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        settings.contrast === option.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-primary/10'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-3">
                {[
                  { key: 'highlightLinks', label: 'Highlight Links', icon: MousePointer2 },
                  { key: 'bigCursor', label: 'Large Cursor', icon: MousePointer2 },
                  { key: 'readableFont', label: 'Readable Font', icon: Type },
                  { key: 'textSpacing', label: 'Text Spacing', icon: Type },
                ].map((option) => (
                  <button
                    key={option.key}
                    onClick={() => setSettings(prev => ({ ...prev, [option.key]: !prev[option.key] }))}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      settings[option.key]
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-primary/10'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <option.icon className="w-4 h-4" />
                      {option.label}
                    </span>
                    <div className={`w-10 h-6 rounded-full transition-colors ${
                      settings[option.key] ? 'bg-primary-foreground/30' : 'bg-foreground/20'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-current transform transition-transform mt-0.5 ${
                        settings[option.key] ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>

              {/* Reset Button */}
              <button
                onClick={resetSettings}
                className="w-full py-3 px-4 rounded-lg border-2 border-primary text-primary font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Reset All Settings
              </button>

              {/* Accessibility Statement Link */}
              <a
                href="/accessibility"
                className="block text-center text-sm text-primary hover:underline"
                onClick={() => setIsOpen(false)}
              >
                Read our Accessibility Statement
              </a>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
